/* eslint-disable react-hooks/exhaustive-deps */
import {
  AtButton,
  AtButtonBehaviors,
  AtButtonDimensions,
  AtButtonTypes,
  Cancel,
  Filter,
  MlNotification,
  MlSpaceCard,
  MlSpaceCardSkeleton,
  NotFoundSpaces,
  OrBottomSheet,
  OrFloatMenu,
  OrPlpNotFindingBanner,
  sortingOptions,
  OrCardCarousel,
  instantBook,
  VIEW_ALL_BUILDING,
  cn,
} from '@studio/atomic-ui'
import type {
  FilterChangeHandler,
  FilterState,
  NotFoundSpacesProps,
  OrPlpNotFindingBannerProps,
  SortOption,
  SpaceType,
  DropdownOption,
  FilterChangeDataType,
} from '@studio/atomic-ui'
import {
  Dispatch,
  ReactNode,
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { DateRange } from 'react-day-picker'
import { mapImage } from '../../services/sanity/client'
import { PlpSortStrategy } from '../../services/api/types'
import { mapSpaceCard, mapNotification } from '../../services/sanity/mappers'
import { SanityAtImage, SanitySmProduct } from '../../services/sanity/types'
import { ProductType } from '../../types/product'
import { useBuildings } from '../buildings-provider'
import { useRouter } from 'next/navigation'
import { trackSpaceCardPropertyResultClick, trackToggleClick } from '../../utils/analytics'
import { LoadingState, useProductShowcaseFetch } from './useProductShowcaseFetch'
import { useProductShowcaseStates } from './useProductShowcaseStates'
import { setAbTestingContextInClarity } from '../../utils/clarity-variant'
import { serializeProductQuery } from './product-query'

// Helper: native date comparison replaces `isEqual` from date-fns, removing
// date-fns from the initial bundle for this file.
const datesEqual = (a: Date | undefined, b: Date | undefined): boolean => a?.getTime() === b?.getTime()

export type FullProduct = SanitySmProduct & {
  isAvailable?: boolean
}

export interface ProductShowcaseProps {
  id?: string
  type?: ProductType // Deprecated: use types instead. Kept for backward compatibility
  types?: ProductType[] // Array of space types. If provided, overrides type
  hideSpaceTypeFilter?: boolean
  building?: string
  region?: string
  capacity?: string
  layout: 'grid' | 'carousel'
  title?: ReactNode
  customTitle?: (type: string, building: string) => ReactNode
  hideDivider?: boolean
  disableSorting?: boolean
  loadMoreLabel?: string
  notFoundCard?: Omit<OrPlpNotFindingBannerProps, 'image'> & { image?: SanityAtImage }
  notFoundView?: NotFoundSpacesProps
  onTypeChange?: (type?: SpaceType) => void
  handler?: Dispatch<FilterState>
  isBDP?: boolean
  isFlexibleTemplate?: boolean
  /** Write the selected type to the URL even when isBDP is true (e.g. location pages) */
  syncTypeToUrl?: boolean
  /** Public query parameter used to persist the selected product types. */
  typeQueryParam?: 'type' | 'product'
  disableRegionFilter: boolean
  disableBuildingFilter: boolean
  /** Server-prefetched initial products — rendered with SSR HTML to eliminate fetch waterfall */
  initialProducts?: SanitySmProduct[]
  /** PLP sort strategy from A/B test: manual uses plpRank, standard uses capacity/price */
  sortStrategy?: PlpSortStrategy
}

const errorMessage = mapNotification({
  title: 'Oops! Something went wrong',
  message: {
    markDefs: [],
    _type: 'block',
    style: 'normal',
    _key: 'api_error_message',
    children: [
      {
        _type: 'span',
        marks: [],
        text: 'Please try again later or contact help@yourstudio.com and a Studio team member will be happy to assist you.',
        _key: 'api_error_message_heading',
      },
    ],
  },
  notificationType: 'error' as const,
  linkLabel: null,
})

const AVAILABILITY_PAGE_SIZE = 8

export const ProductShowcase = (props: ProductShowcaseProps) => {
  const { buildings, activeBuildings } = useBuildings()
  const {
    state,
    handler,
    buildingListCall,
    processing,
    setProcessing,
    bookingPanelOptions,
    setBookingPanelOptions,
    clearFiltersState,
    initialBuildingFromProps,
    initialRegionFromProps,
  } = useProductShowcaseStates(props)

  const router = useRouter()

  const isPLPFirstUpdateRoute = useRef(true)
  const [sortBy, setSortBy] = useState<SortOption>(sortingOptions[2])
  const [results, setResults] = useState<Array<SanitySmProduct>>(props.initialProducts ?? [])
  const [pageUsingAvailability, setPageUsingAvailability] = useState(1)
  const loadMoreWithAvailability = () => setPageUsingAvailability((prev) => prev + 1)

  const currentTypes = useMemo<SpaceType[]>(() => {
    if (state.types && state.types.length > 0) {
      return state.types
    }
    if (state.type) {
      return [state.type]
    }
    return ['meeting_room']
  }, [state.types, state.type])

  // Stable ref-based callback so that onProductsUpdate identity never changes,
  // preventing fetchProductsPaginated from being recreated on every render.
  const onProductsUpdateRef = useRef<((products: SanitySmProduct[]) => Promise<void>) | undefined>(undefined)
  const stableOnProductsUpdate = useCallback(
    async (products: SanitySmProduct[]) => onProductsUpdateRef.current?.(products),
    []
  )

  const { productsSize, loadingState, fetchAllProducts, fetchProductsPaginated, buildingHolidays, businessHours } =
    useProductShowcaseFetch({
      instantBook: state.instantBook ?? false,
      location: state.location?.value,
      building: state.building?.id,
      type: state.type, // Backward compat
      types: currentTypes.length > 0 ? currentTypes : undefined,
      propsBuilding: props.building,
      propsRegion: props.region,
      capacity: state.capacity,
      sortBy,
      buildingListCall,
      onProductsUpdate: stableOnProductsUpdate,
      initialProducts: props.initialProducts,
      sortStrategy: props.sortStrategy ?? PlpSortStrategy.Standard,
    })

  /** Product card size setting between mobile and desktop */
  const [size, setSize] = useState<'small' | 'medium'>('small')
  useEffect(() => {
    const boundary = window.matchMedia('(min-width: 1440px)')
    const sizeChange = () => setSize(window.visualViewport && window.visualViewport.width >= 1440 ? 'small' : 'medium')

    boundary.addEventListener('change', sizeChange)
    return () => boundary.removeEventListener('change', sizeChange)
  }, [])

  /** Fetch availability only if there are new changes in parameters */
  const validFetchStates = (data: FilterChangeDataType) => {
    // Use values from data if available, otherwise use state
    const effectiveDate = data.date || state.date
    // Get effective types - prefer types array, fallback to single type
    const effectiveTypes = (() => {
      if (data.types && data.types.length > 0) return data.types
      if (data.type) return [data.type]
      if (state.types && state.types.length > 0) return state.types
      if (state.type) return [state.type]
      return ['meeting_room']
    })()
    const effectiveDuration = data.duration?.value || state.duration?.value
    const effectiveStartTime = data.startTime?.value || state.startTime?.value

    // should fetch when date is a range, and they were not yet processed
    const range =
      effectiveDate &&
      'to' in effectiveDate &&
      effectiveDate?.to &&
      effectiveDate?.from &&
      ((processing.to &&
        processing.from &&
        (!datesEqual(effectiveDate.to, processing.to) || !datesEqual(effectiveDate.from, processing.from))) ||
        !processing.to ||
        !processing.from)

    // should fetch when a starting date is available and hasn't been fetched, and is any space type other than meeting room
    // Check if any selected type is NOT meeting_room
    const hasNonMeetingRoomTypes = effectiveTypes.some((type) => type !== 'meeting_room')
    const singleOther =
      effectiveDate &&
      'from' in effectiveDate &&
      hasNonMeetingRoomTypes &&
      !!effectiveDate?.from &&
      (!processing.from || !datesEqual(effectiveDate.from, processing.from))

    // same as previous, except that for meeting room it checks for start time and duration
    // Check if meeting_room is among selected types
    const hasMeetingRoom = effectiveTypes.includes('meeting_room')
    const singleMeeting =
      hasMeetingRoom &&
      effectiveDate &&
      'from' in effectiveDate &&
      effectiveDate.from &&
      effectiveDuration &&
      effectiveStartTime &&
      (effectiveDuration !== processing.duration ||
        effectiveStartTime !== processing.startTime ||
        !datesEqual(effectiveDate?.from, processing.from as Date))

    return Boolean(range || singleOther || singleMeeting)
  }

  const isFilteringByAvailability =
    !state.calendarOpen &&
    !state.disabled &&
    validFetchStates({
      duration: state.duration?.value ? state.duration : { label: '30 minutes', value: '0.5' },
      startTime: state.startTime?.value
        ? state.startTime
        : {
            label: `${businessHours.startHour} ${
              Number(businessHours.startHour.split(':')[0]) >= 12 ? 'p.m.' : 'a.m.'
            }`,
            value: businessHours.startHour,
          },
    })

  const deferredResults = useDeferredValue(results)
  const displayedResults = useMemo(
    () =>
      isFilteringByAvailability
        ? deferredResults.slice(0, pageUsingAvailability * AVAILABILITY_PAGE_SIZE)
        : deferredResults,
    [deferredResults, isFilteringByAvailability, pageUsingAvailability]
  )

  const carouselCards = useMemo(
    () =>
      results?.map((item) => {
        const cardProps = mapSpaceCard(item, { sortStrategy: props.sortStrategy, buildings })
        return {
          ...cardProps,
          href: bookingPanelOptions
            ? `${cardProps.href}?${bookingPanelOptions}${item.inquiryOnly ? '&inquire=true' : ''}`
            : cardProps.href,
          images: cardProps.images.map((image) => mapImage(image, { width: 254 })),
          onClick: () => {
            let ab = null
            if (item.spaceABTestingConfig) {
              const cookieValue =
                item.spaceABTestingConfig.spaceVariants.find(
                  (variant) => variant.variantName === item.spaceABTestingConfig?.variantNameSelected
                )?.spaceDetails[0].key || ''
              ab = {
                variant: item.spaceABTestingConfig?.variantNameSelected || '',
                cookieName: item.spaceABTestingConfig?.cookieName || '',
                value: cookieValue,
              }
              setAbTestingContextInClarity(
                'or-card-carousel',
                item.spaceABTestingConfig?.variantNameSelected || '',
                item.spaceABTestingConfig?.cookieName || '',
                cookieValue
              )
            }
            trackSpaceCardPropertyResultClick(
              {
                spaceType: cardProps.space,
                building: cardProps.building,
                title: cardProps.title,
                businessRegion: state.location?.value || '',
                targetUrl: cardProps.href,
                windowLocationHref: window.location.href,
                componentName: 'or-card-carousel',
              },
              ab
            )
          },
        }
      }) ?? [],
    [results, bookingPanelOptions, state.location?.value, props.sortStrategy]
  )

  const updateResults = useCallback(
    async (products: SanitySmProduct[]) => {
      const filtering = async () => {
        if (isFilteringByAvailability) {
          // Lazy-load availability utils — they pull in date-fns and are only
          // needed when the user interacts with the date/availability filter.
          const { isBookableWithinDate, preparePdpQueryParams } = await import('./showcase-utils')

          setPageUsingAvailability(1)
          setBookingPanelOptions(preparePdpQueryParams(state))
          setProcessing({
            types: currentTypes.length > 0 ? currentTypes : undefined,
            type: currentTypes.length === 1 ? currentTypes[0] : undefined, // Backward compat
            to: (state.date as DateRange).to,
            from: (state.date as DateRange).from,
            duration: state.duration?.value,
            startTime: state.startTime?.value,
            changedType: false,
          })
          const update = await Promise.all<FullProduct>(
            products.map(async (product) => {
              if (product.platformId) {
                const availabilityInfo = await isBookableWithinDate(product, state, product.slug.split('/')[0])
                return { ...product, ...availabilityInfo }
              } else return { ...product, isAvailable: false }
            })
          )

          const filteredProducts = update.filter((product) =>
            state.instantBook ? product.isAvailable && !product.inquiryOnly : product.isAvailable
          )
          startTransition(() => setResults(filteredProducts))
        } else {
          const list = products?.length ? products : []
          startTransition(() => setResults(list))
        }
      }

      handler({ loading: true })
      try {
        await filtering()
      } finally {
        handler({ loading: false })
      }
    },
    [
      isFilteringByAvailability,
      state,
      currentTypes,
      handler,
      setBookingPanelOptions,
      setPageUsingAvailability,
      setProcessing,
    ]
  )

  // Keep the stable ref in sync with the latest updateResults
  onProductsUpdateRef.current = updateResults

  useEffect(() => {
    if (isFilteringByAvailability) fetchAllProducts()
  }, [
    state.location,
    state.building,
    state.type,
    state.types,
    state.instantBook,
    state.duration,
    state.startTime,
    state.date,
    state.calendarOpen,
    sortBy,
  ])

  useEffect(() => {
    if (!isFilteringByAvailability) fetchProductsPaginated()
  }, [state.location, state.building, state.type, state.types, sortBy, state.capacity, state.instantBook])

  const handleLocationChange = (location: DropdownOption | undefined, building: DropdownOption | undefined) => {
    handler({
      disabled: (!props.isBDP && !building?.id) || building?.id === 'all',
      location,
      building: VIEW_ALL_BUILDING,
    })
  }

  const handleBuildingChange = (building: DropdownOption | undefined) => {
    handler({
      disabled: (!props.isBDP && !building?.id) || building?.id === 'all',
      building,
    })
  }

  const handleTypeChange = (type: SpaceType) => {
    // Legacy function for backward compatibility - now handled by Filter component toggle
    setResults([])
    setBookingPanelOptions('')
    handler({
      types: [type],
      type, // Backward compat
      capacity: {
        label: '',
        value: '',
      },
      duration: {
        label: '',
        value: '',
      },
      date: undefined,
      startTime: {
        label: '',
        value: '',
      },
    })
  }

  const clearFilters = useCallback(() => {
    setBookingPanelOptions('')
    clearFiltersState()
  }, [clearFiltersState])

  useEffect(() => {
    if (state.isModified) {
      updateRouter()
    }
  }, [state.building, state.location, state.type, state.types, state.capacity])

  const updateRouter = () => {
    const { location, building, capacity, type, types } = state
    let newLocation = location?.value
    let newBuilding = building?.id
    // Get types array, handling backward compatibility
    const currentTypes: ProductType[] = (() => {
      if (types && types.length > 0) return types
      if (type) return [type]
      return ['meeting_room']
    })()

    if (
      newLocation === 'all' ||
      props.isBDP ||
      (props.isFlexibleTemplate && newLocation === initialRegionFromProps.value)
    ) {
      newLocation = undefined
    }

    if (
      newBuilding === 'all' ||
      newLocation === 'all' ||
      props.isBDP ||
      (props.isFlexibleTemplate && newBuilding === initialBuildingFromProps.value)
    ) {
      newBuilding = undefined
    }

    let newType: string | undefined = undefined
    if ((!props.isBDP && !props.isFlexibleTemplate) || props.syncTypeToUrl) {
      if (currentTypes.length > 0) {
        if (currentTypes.length === 1 && currentTypes[0] !== 'meeting_room') {
          isPLPFirstUpdateRoute.current = false
          newType = currentTypes[0]
        } else if (currentTypes.length > 1) {
          // Multiple types: use comma-separated format
          isPLPFirstUpdateRoute.current = false
          newType = currentTypes.join(',')
        } else if (isPLPFirstUpdateRoute.current) {
          // Single meeting_room: don't add to URL initially
          newType = undefined
        } else {
          newType = currentTypes[0]
        }
      }
    }

    if (props.hideSpaceTypeFilter) {
      newType = undefined
    }

    const searchParams = new URLSearchParams()
    if (newLocation) searchParams.set('location', newLocation)
    if (newBuilding) searchParams.set('building', newBuilding)
    if (newType) {
      const queryParam = props.typeQueryParam ?? 'type'
      const queryValue = queryParam === 'product' ? serializeProductQuery(currentTypes) : newType
      if (queryValue) searchParams.set(queryParam, queryValue)
    }
    if (capacity?.value) searchParams.set('capacity', capacity.value)

    const search = searchParams.toString()
    router.replace(`${window.location.pathname}${search ? `?${search}` : ''}`, { scroll: false })
  }

  const onFilterChange: FilterChangeHandler = (data) => {
    if (typeof data.instantBook === 'boolean') {
      trackToggleClick({
        value: data.instantBook,
        titleDescriptor: instantBook.label,
        componentName: 'toggle',
        windowLocationHref: window.location.href,
      })
    }
    // Wrap state updates in startTransition so the browser can paint the
    // interaction feedback (e.g. toggle/button visual change) before React
    // processes the expensive product list re-render.
    startTransition(() => {
      // Handle types array changes (new multi-select)
      if (
        data.types !== undefined &&
        (loadingState === LoadingState.IDLE || loadingState === LoadingState.LOAD_ERROR)
      ) {
        setResults([])
        setBookingPanelOptions('')
        handler({
          ...data,
          capacity: {
            label: '',
            value: '',
          },
          duration: {
            label: '',
            value: '',
          },
          date: undefined,
          startTime: {
            label: '',
            value: '',
          },
        })
        // Call onTypeChange with first type for backward compatibility
        if (props.onTypeChange && data.types.length > 0) {
          props.onTypeChange(data.types[0])
        }
      } else if (data.type && (loadingState === LoadingState.IDLE || loadingState === LoadingState.LOAD_ERROR)) {
        // Legacy single type change (backward compatibility)
        handleTypeChange(data.type)
        if (props.onTypeChange) props.onTypeChange(data.type)
      } else if (!data.type && !data.types) {
        const range = data.date as DateRange | undefined

        if (state.type === 'meeting_room' && range?.from && !range.to) {
          // Determine duration: prefer data, then state, then default
          let duration = { label: '30 minutes', value: '0.5' }
          if (data.duration?.value) {
            duration = data.duration
          } else if (state.duration?.value) {
            duration = state.duration
          }

          // Determine startTime: prefer data, then state, then default from business hours
          let startTime = {
            label: `${businessHours.startHour} ${
              Number(businessHours.startHour.split(':')[0]) >= 12 ? 'p.m.' : 'a.m.'
            }`,
            value: businessHours.startHour,
          }
          if (data.startTime?.value) {
            startTime = data.startTime
          } else if (state.startTime?.value) {
            startTime = state.startTime
          }

          handler({
            ...data,
            duration,
            startTime,
          })

          return
        }
        if (data.location && data.building) {
          handleLocationChange(data.location, data.building)
        } else if (data.building) {
          handleBuildingChange(data.building)
        } else {
          handler({ ...data })
        }
      }
    })
  }

  return (
    <section className="pt-8 sm:px-4 slg:px-8 lg:px-0">
      <div className={`lg:max-w-screen-lg mx-auto lg:px-[10.44rem] ${props.layout === 'carousel' ? '' : 'mb-10'}`}>
        {props.customTitle ? props.customTitle(state.type ?? '', state.building?.label ?? '') : props.title}
        <Filter
          hideSpaceTypeFilter={props.hideSpaceTypeFilter}
          buildings={activeBuildings}
          state={{
            ...state,
            loading:
              loadingState === LoadingState.FETCHING_MORE_PRODUCTS ||
              loadingState === LoadingState.FETCHING_NEW_PRODUCTS,
          }}
          onChange={onFilterChange}
          isBDP={props.isBDP}
          isFlexibleTemplate={props.isFlexibleTemplate}
          holidays={buildingHolidays.data}
          businessHours={businessHours}
          disableBuildingFilter={props.disableBuildingFilter}
          disableRegionFilter={props.disableRegionFilter}
        />
      </div>

      {!props.hideDivider && <div aria-hidden className="h-[1px] border-t-1 border-neutrals-400" />}

      <div className="lg:mx-auto lg:max-w-screen-lg flex flex-col items-center relative">
        {loadingState !== LoadingState.LOAD_ERROR && (
          <>
            {results.length > 0 ||
            loadingState === LoadingState.FETCHING_MORE_PRODUCTS ||
            loadingState === LoadingState.FETCHING_NEW_PRODUCTS ? (
              <div
                className={`${props.layout === 'carousel' ? 'w-full' : 'pb-10'} ${props.disableSorting ? '' : 'pt-8'}`}
              >
                {!props.disableSorting && (
                  <div className="pb-8 lg:pb-4 px-2 lg:pl-6 lg:pr-3 flex flex-col lg:flex-row justify-between lg:items-center">
                    {loadingState === LoadingState.IDLE && (
                      <>
                        <div className="mb-3 lg:mb-0 relative">
                          <p className="leading-1.5 text-neutrals-800 mb-1 lg:mb-0 inline">
                            {isFilteringByAvailability
                              ? `1 to ${Math.min(pageUsingAvailability * AVAILABILITY_PAGE_SIZE, results.length)} of ${
                                  results.length
                                } results`
                              : `1 to ${results.length} of ${productsSize} results`}
                          </p>
                          <button
                            aria-label="Clear Filters"
                            className="text-16 inline text-neutrals-900 ml-8 absolute lg:static right-0"
                            onClick={clearFilters}
                            type={AtButtonBehaviors.RESET}
                          >
                            Clear Filters <Cancel className="inline pb-[0.125rem] ml-1" />
                          </button>
                        </div>
                        <OrFloatMenu options={sortingOptions} onSelect={setSortBy} selected={sortBy} />
                        <OrBottomSheet options={sortingOptions} onSelect={setSortBy} selected={sortBy} />
                      </>
                    )}
                  </div>
                )}

                {props.layout === 'carousel' && (
                  <div className="pt-8 md:pr-[1.125rem] slg:pr-0 lg:pt-8 lg:pl-[9.4375rem] lg:pr-[9.47rem] lg:max-w-screen-lg lg:mx-auto">
                    <button
                      aria-label="Clear Filters"
                      className={cn(
                        'text-16 inline ml-4 absolute lg:static left-0',
                        loadingState !== LoadingState.IDLE && 'invisible'
                      )}
                      onClick={clearFilters}
                      type={AtButtonBehaviors.RESET}
                    >
                      Clear Filters <Cancel className="inline pb-[0.125rem] ml-1" />
                    </button>
                    <div className="pt-8 lg:pt-0">
                      <OrCardCarousel
                        cards={carouselCards}
                        loading={loadingState === LoadingState.FETCHING_NEW_PRODUCTS}
                        size="medium"
                        slideClassName="md:first:pl-[0] lg:!w-[17.58rem]"
                        buttonPositioning={` flex right-[3rem] sm:right-4 md:right-3 -top-[3.6rem] slg:!right-[1.063rem] slg:-top-[8rem] lg:-top-[7.5rem] ${
                          state?.type === 'suite' ? 'slg:!-top-[3.5rem] lg:!-top-[3rem]' : ''
                        }`}
                        embedded
                        className="pl-0"
                        loadMore={fetchProductsPaginated}
                        productsSize={productsSize}
                      />
                    </div>
                  </div>
                )}

                {props.layout === 'grid' && (
                  <div
                    className={`grid grid-cols-1 lg:grid-cols-4 gap-y-6 lg:gap-y-8 ${
                      loadingState === LoadingState.FETCHING_NEW_PRODUCTS ? 'lg:gap-x-[2.675rem]' : ''
                    }`}
                  >
                    {results.length > 0 &&
                      loadingState !== LoadingState.FETCHING_NEW_PRODUCTS &&
                      displayedResults.map((result, index) => {
                        const cardProps = mapSpaceCard(result, { sortStrategy: props.sortStrategy, buildings })
                        const uniqueKey = `${cardProps.href}-${index}`

                        return (
                          <MlSpaceCard
                            key={uniqueKey}
                            {...cardProps}
                            href={
                              bookingPanelOptions
                                ? `${cardProps.href}?${bookingPanelOptions}${result.inquiryOnly ? '&inquire=true' : ''}`
                                : cardProps.href
                            }
                            images={cardProps.images?.map((image) => mapImage(image, { width: 254 }))}
                            lazy={index >= 4}
                            size={size}
                            onClick={() => {
                              let ab = null
                              if (result.spaceABTestingConfig) {
                                const cookieValue =
                                  result.spaceABTestingConfig.spaceVariants.find(
                                    (variant) =>
                                      variant.variantName === result.spaceABTestingConfig?.variantNameSelected
                                  )?.spaceDetails[0].key || ''

                                ab = {
                                  variant: result.spaceABTestingConfig.variantNameSelected || '',
                                  cookieName: result.spaceABTestingConfig.cookieName || '',
                                  value: cookieValue,
                                }

                                setAbTestingContextInClarity(
                                  'ml-space-card',
                                  result.spaceABTestingConfig.variantNameSelected || '',
                                  result.spaceABTestingConfig.cookieName || '',
                                  cookieValue
                                )
                              }

                              trackSpaceCardPropertyResultClick(
                                {
                                  spaceType: cardProps.space,
                                  building: cardProps.building,
                                  title: cardProps.title,
                                  businessRegion: state.location?.value || '',
                                  targetUrl: cardProps.href,
                                  windowLocationHref: window.location.href,
                                  componentName: 'ml-space-card',
                                },
                                ab
                              )
                            }}
                          />
                        )
                      })}
                    {(loadingState === LoadingState.FETCHING_MORE_PRODUCTS ||
                      loadingState === LoadingState.FETCHING_NEW_PRODUCTS) &&
                      Array.from({ length: 8 }, (_, i) => <MlSpaceCardSkeleton key={i} size={size} />)}
                  </div>
                )}
              </div>
            ) : null}

            {results.length === 0 && loadingState === LoadingState.IDLE && (
              <div className="pt-8 pl-4 lg:w-full lg:px-[10.44rem]">
                <button
                  aria-label="Clear Filters"
                  className="text-16 inline text-neutrals-800"
                  onClick={clearFilters}
                  type={AtButtonBehaviors.RESET}
                >
                  Clear Filters <Cancel className="inline pb-[0.125rem] ml-1" />
                </button>
                <NotFoundSpaces {...props.notFoundView} />
              </div>
            )}
          </>
        )}

        {loadingState === LoadingState.LOAD_ERROR && <MlNotification {...errorMessage} className="mt-8 mb-6" />}

        {(isFilteringByAvailability
          ? pageUsingAvailability * AVAILABILITY_PAGE_SIZE < results.length
          : results.length < productsSize) &&
          loadingState === LoadingState.IDLE &&
          results.length !== 0 &&
          props.layout === 'grid' && (
            <div className="pt-12 pb-8 lg:py-10">
              <AtButton
                id={props.id ? `${props.id}-load-more-button` : undefined}
                type={AtButtonTypes.SECONDARY}
                size={AtButtonDimensions.MEDIUM}
                title={props.loadMoreLabel ? props.loadMoreLabel : 'Load more'}
                onClick={isFilteringByAvailability ? loadMoreWithAvailability : fetchProductsPaginated}
                parentName="ProductShowcase"
                className="px-8"
              />
            </div>
          )}

        {props.notFoundCard && (
          <div className="mt-8">
            <OrPlpNotFindingBanner
              {...props.notFoundCard}
              image={{
                ...mapImage(props.notFoundCard.image, { width: 316 }),
                alt: props.notFoundCard.image?.alt || props.notFoundCard.title,
              }}
            />
          </div>
        )}
      </div>
    </section>
  )
}
